<template>
  <PageHeader
    breadcrumb
    :items="[{ label: $t('ui.entities.setting', 2), to: '/settings' }, { label: 'Cron Jobs' }]"
  ></PageHeader>

  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      :value="cronjobs"
      removable-sort
      sort-field="id"
      :rows="20"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-settings-admin-cronjobs"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :global-filter-fields="['id', 'name', 'slug', 'cronExpression', 'scriptPath']"
      :loading="cronjobsApiRequest.isLoading.value"
    >
      <template #empty>No records found</template>
      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="globalFilter"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <!-- <div class="flex justify-between mt-4">
          <span>
            <Button label="CSV" type="button" severity="secondary" outlined icon="file-excel" @click="exportCSV($event)" />
          </span>
        </div> -->
      </template>

      <Column
        field="id"
        header="ID"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.id }}
        </template>
      </Column>
      <Column
        field="name"
        header="Name"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.name }}
        </template>
      </Column>
      <Column
        field="cronExpression"
        header="Cron Expression"
        :sortable="true"
      >
        <template #body="{ data }">
          <span>{{ data.cronExpression }}</span>
        </template>
      </Column>
      <Column
        field="timezone"
        header="Timezone"
        :sortable="true"
      >
        <template #body="{ data }">
          <span>{{ data.timezone || '-' }}</span>
        </template>
      </Column>
      <Column
        field="scriptPath"
        header="Script"
        :sortable="true"
      >
        <template #body="{ data }">
          <span
            v-tippy="data.scriptPath"
            class="block max-w-[360px] truncate font-mono text-xs"
          >
            {{ data.scriptPath }}
          </span>
        </template>
      </Column>
      <Column
        field="lastStatus"
        header="Last Status"
        :sortable="true"
      >
        <template #body="{ data }">
          <span
            class="inline-flex rounded-full px-2 py-1 text-xs font-medium"
            :class="statusClass(data.lastStatus)"
          >
            {{ formatStatus(data.lastStatus) }}
          </span>
        </template>
      </Column>
      <Column
        field="lastFinishedAt"
        header="Last Run"
        :sortable="true"
      >
        <template #body="{ data }">
          <span
            v-if="data.lastFinishedAt"
            v-tippy="dayjs(data.lastFinishedAt).format('lll')"
          >
            {{ dayjs(data.lastFinishedAt).from(dayjs()) }}
          </span>
          <span v-else>-</span>
        </template>
      </Column>
      <Column
        field="lastErrorAt"
        header="Last Error At"
        :sortable="true"
      >
        <template #body="{ data }">
          <span v-if="data.lastErrorAt">
            <span v-tippy="dayjs(data.lastErrorAt).format('lll')">{{ dayjs(data.lastErrorAt).from(dayjs()) }}</span>
          </span>
          <span v-else>-</span>
        </template>
      </Column>
      <Column
        field="isActive"
        header="Status"
        :sortable="true"
      >
        <template #body="{ data }">
          <ToggleSwitch
            v-model="data.isActive"
            :disabled="statusUpdateIds.includes(data.id)"
            @change="updateStatus(data, $event)"
          />
        </template>
      </Column>
      <Column header="Actions">
        <template #body="{ data }">
          <div class="flex gap-2">
            <Button
              v-tippy="'View recent runs'"
              icon="list"
              severity="secondary"
              size="sm"
              plain
              @click="openRunsModal(data)"
            />
            <Button
              v-tippy="'View recent errors'"
              icon="triangle-exclamation"
              severity="secondary"
              size="sm"
              plain
              @click="openErrorsModal(data)"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </Card>

  <Modal
    :is-open="runsModalIsOpen"
    :title="selectedCronJob ? `Recent Runs - ${selectedCronJob.name}` : 'Recent Runs'"
    :description="selectedCronJob?.scriptPath || undefined"
    :buttons="['close']"
    min-width="950"
    max-width="1150"
    icon="list"
    @close-modal="closeRunsModal"
  >
    <DataTable
      :value="cronJobRuns"
      :loading="cronJobRunsApiRequest.isLoading.value"
      data-key="id"
      responsive-layout="scroll"
      removable-sort
      sort-field="startedAt"
      :sort-order="-1"
      class="cursor-pointer"
      @row-click="openRunDetail($event.data)"
    >
      <template #empty>No runs found</template>
      <Column
        field="id"
        header="Run ID"
        :sortable="true"
      />
      <Column
        field="startedAt"
        header="Started"
        :sortable="true"
      >
        <template #body="{ data }">{{ formatDateTime(data.startedAt) }}</template>
      </Column>
      <Column
        field="finishedAt"
        header="Finished"
        :sortable="true"
      >
        <template #body="{ data }">{{ formatDateTime(data.finishedAt) }}</template>
      </Column>
      <Column
        field="status"
        header="Status"
        :sortable="true"
      >
        <template #body="{ data }">
          <span
            class="inline-flex rounded-full px-2 py-1 text-xs font-medium"
            :class="statusClass(data.status)"
          >
            {{ formatStatus(data.status) }}
          </span>
        </template>
      </Column>
      <Column
        field="durationMs"
        header="Duration"
        :sortable="true"
      >
        <template #body="{ data }">{{ formatDuration(data.durationMs) }}</template>
      </Column>
      <Column
        field="triggerSource"
        header="Trigger"
        :sortable="true"
      >
        <template #body="{ data }">{{ formatStatus(data.triggerSource) }}</template>
      </Column>
    </DataTable>
  </Modal>

  <Modal
    :is-open="errorsModalIsOpen"
    :title="selectedCronJob ? `Recent Errors - ${selectedCronJob.name}` : 'Recent Errors'"
    :description="selectedCronJob?.scriptPath || undefined"
    :buttons="['close']"
    min-width="950"
    max-width="1150"
    icon="triangle-exclamation"
    @close-modal="closeErrorsModal"
  >
    <DataTable
      :value="cronJobErrors"
      :loading="cronJobErrorsApiRequest.isLoading.value"
      data-key="id"
      responsive-layout="scroll"
      removable-sort
      sort-field="createdAt"
      :sort-order="-1"
      class="cursor-pointer"
      @row-click="openErrorDetail($event.data)"
    >
      <template #empty>No errors found</template>
      <Column
        field="id"
        header="Error ID"
        :sortable="true"
      />
      <Column
        field="createdAt"
        header="Created"
        :sortable="true"
      >
        <template #body="{ data }">{{ formatDateTime(data.createdAt) }}</template>
      </Column>
      <Column
        field="cronJobRunId"
        header="Run ID"
        :sortable="true"
      >
        <template #body="{ data }">{{ data.cronJobRunId || '-' }}</template>
      </Column>
      <Column
        field="category"
        header="Category"
        :sortable="true"
      >
        <template #body="{ data }">{{ formatStatus(data.category) }}</template>
      </Column>
      <Column
        field="errorMessage"
        header="Message"
      >
        <template #body="{ data }">
          <span
            v-tippy="data.errorMessage"
            class="block max-w-[480px] truncate"
          >
            {{ data.errorMessage }}
          </span>
        </template>
      </Column>
    </DataTable>
  </Modal>

  <teleport to="#modals-container">
    <div
      v-if="runDetailModalIsOpen && selectedRun"
      class="cron-fullscreen-backdrop"
    >
      <div class="cron-fullscreen-modal">
        <div class="mb-6 flex items-start justify-between gap-6">
          <div>
            <div class="text-xl font-semibold">Run #{{ selectedRun.id }}</div>
            <div class="text-sm text-secondary">{{ selectedCronJob?.name }}</div>
          </div>
          <Button
            icon="times"
            severity="secondary"
            plain
            @click="closeRunDetail"
          />
        </div>

        <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div
            v-for="item in runDetailItems"
            :key="item.label"
            class="rounded-xl border border-secondary p-4"
          >
            <div class="mb-1 text-xs uppercase text-tertiary">{{ item.label }}</div>
            <div class="wrap-break-word font-medium">{{ item.value }}</div>
          </div>
        </div>

        <div class="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div class="rounded-xl border border-secondary p-4">
            <div class="mb-3 font-semibold">Output Preview</div>
            <pre class="cron-detail-pre">{{ formatOutputPreview(selectedRun.outputPreview) }}</pre>
          </div>
          <div class="rounded-xl border border-secondary p-4">
            <div class="mb-3 font-semibold">Error</div>
            <pre class="cron-detail-pre">{{ selectedRun.error || '-' }}</pre>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="errorDetailModalIsOpen && selectedError"
      class="cron-fullscreen-backdrop"
    >
      <div class="cron-fullscreen-modal">
        <div class="mb-6 flex items-start justify-between gap-6">
          <div>
            <div class="text-xl font-semibold">Error #{{ selectedError.id }}</div>
            <div class="text-sm text-secondary">{{ selectedCronJob?.name }}</div>
          </div>
          <Button
            icon="times"
            severity="secondary"
            plain
            @click="closeErrorDetail"
          />
        </div>

        <div class="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div
            v-for="item in errorDetailItems"
            :key="item.label"
            class="rounded-xl border border-secondary p-4"
          >
            <div class="mb-1 text-xs uppercase text-tertiary">{{ item.label }}</div>
            <div class="wrap-break-word font-medium">{{ item.value }}</div>
          </div>
        </div>

        <div class="mt-6 rounded-xl border border-secondary p-4">
          <div class="mb-3 font-semibold">Error Message</div>
          <pre class="cron-detail-pre">{{ selectedError.errorMessage }}</pre>
        </div>

        <div class="mt-6 rounded-xl border border-secondary p-4">
          <div class="mb-3 font-semibold">Context</div>
          <pre class="cron-detail-pre">{{ formatJson(selectedError.context) }}</pre>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
// composables
import { computed, ref } from 'vue'
import useApiRequest from '@/composables/useApiRequest'
// components
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import Button from '@/components/button/Button.vue'
import Input from '@/components/forms/Input.vue'
import Modal from '@/components/modal/Modal.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
// utilities
import { alertToast } from '@/utilities/notification'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import { FilterMatchMode } from '@primevue/core/api'
import dayjs from 'dayjs'

interface CronJob {
  id: number
  name: string
  slug: string | null
  description: string | null
  scriptPath: string
  cronExpression: string
  timezone: string
  isActive: boolean
  lastStartedAt: string | null
  lastFinishedAt: string | null
  lastStatus: string | null
  lastExitCode: number | null
  lastErrorAt: string | null
  createdAt: string
  updatedAt: string
}

interface CronJobRun {
  id: number
  cronJobId: number
  startedAt: string
  finishedAt: string | null
  status: 'success' | 'failed' | 'skipped' | 'invalid_config'
  exitCode: number | null
  durationMs: number | null
  triggerSource: 'scheduler' | 'manual' | 'legacy_migration'
  createdAt: string
  outputPreview?: string[]
  error?: string
}

interface CronJobError {
  id: number
  cronJobId: number
  cronJobRunId: number | null
  category: string
  errorMessage: string
  context: Record<string, unknown> | null
  createdAt: string
}

interface DetailItem {
  label: string
  value: string | number
}

interface FilterValue {
  value: string | number[] | undefined
  matchMode: string
}

const cronjobs = ref<CronJob[]>([])
const cronJobRuns = ref<CronJobRun[]>([])
const cronJobErrors = ref<CronJobError[]>([])
const selectedCronJob = ref<CronJob | null>(null)
const selectedRun = ref<CronJobRun | null>(null)
const selectedError = ref<CronJobError | null>(null)
const statusUpdateIds = ref<number[]>([])
const runsModalIsOpen = ref(false)
const errorsModalIsOpen = ref(false)
const runDetailModalIsOpen = ref(false)
const errorDetailModalIsOpen = ref(false)
const cronjobsApiRequest = useApiRequest()
const cronJobRunsApiRequest = useApiRequest()
const cronJobErrorsApiRequest = useApiRequest()

const filters = ref<Record<string, FilterValue>>({})

/**
 * Bridge PrimeVue's flexible filter value to the string-only Input model.
 */
const globalFilter = computed<string | undefined>({
  get: () => {
    const value = filters.value.global?.value

    return typeof value === 'string' ? value : undefined
  },
  set: value => {
    filters.value.global.value = value
  }
})

/**
 * Initialize table filters.
 */
function initFilters() {
  filters.value = {
    global: { value: undefined, matchMode: FilterMatchMode.CONTAINS },
    id: { value: undefined, matchMode: FilterMatchMode.IN },
    missingInfo: { value: undefined, matchMode: FilterMatchMode.IN }
  }
}
initFilters()

/**
 * Clear all table filters.
 */
const clearFilters = () => {
  initFilters()
}

/**
 * Load cron jobs and normalize legacy/new API shapes.
 */
const loadCronjobs = async () => {
  try {
    const response = await cronjobsApiRequest.send<CronJob[]>({
      endpoint: '/v1/admin/cronjobs'
    })

    // The API returns the list in the normalized response data.
    cronjobs.value = Array.isArray(response.data) ? response.data : []
  } catch {
    cronjobs.value = []
  }
}

loadCronjobs()

/**
 * Update a cron job's active state.
 *
 * @param {CronJob} job - Cron job row being updated.
 * @param {boolean} nextStatus - New active state emitted by the toggle.
 */
const updateStatus = async (job: CronJob, nextStatus: boolean) => {
  const previousStatus = !nextStatus
  statusUpdateIds.value = [...statusUpdateIds.value, job.id]

  try {
    const response = await useApiRequest().send({
      endpoint: `/v1/admin/cronjobs/${job.id}`,
      method: 'PATCH',
      data: {
        isActive: nextStatus
      }
    })

    // Revert the optimistic toggle when the API rejects the update.
    if (!response.success) {
      job.isActive = previousStatus
      alertToast('CronJob status not updated', response.message, 'error')
      return
    }

    job.isActive = nextStatus
    alertToast('CronJob status updated', null, 'success')
  } catch {
    job.isActive = previousStatus
  } finally {
    statusUpdateIds.value = statusUpdateIds.value.filter(id => id !== job.id)
  }
}

/**
 * Open the recent runs modal for a cron job.
 *
 * @param {CronJob} job - Cron job to load runs for.
 */
const openRunsModal = async (job: CronJob) => {
  selectedCronJob.value = job
  cronJobRuns.value = []
  runsModalIsOpen.value = true

  try {
    const response = await cronJobRunsApiRequest.send<CronJobRun[]>({
      endpoint: `/v1/admin/cronjobs/${job.id}/runs`
    })

    cronJobRuns.value = Array.isArray(response.data) ? response.data : []
  } catch {
    cronJobRuns.value = []
  }
}

/**
 * Close the recent runs modal and detail view.
 */
const closeRunsModal = () => {
  runsModalIsOpen.value = false
  closeRunDetail()
}

/**
 * Open the recent errors modal for a cron job.
 *
 * @param {CronJob} job - Cron job to load errors for.
 */
const openErrorsModal = async (job: CronJob) => {
  selectedCronJob.value = job
  cronJobErrors.value = []
  errorsModalIsOpen.value = true

  try {
    const response = await cronJobErrorsApiRequest.send<CronJobError[]>({
      endpoint: `/v1/admin/cronjobs/${job.id}/errors`
    })

    cronJobErrors.value = Array.isArray(response.data) ? response.data : []
  } catch {
    cronJobErrors.value = []
  }
}

/**
 * Close the recent errors modal and detail view.
 */
const closeErrorsModal = () => {
  errorsModalIsOpen.value = false
  closeErrorDetail()
}

/**
 * Open a full-screen run detail modal.
 *
 * @param {CronJobRun} run - Selected run.
 */
const openRunDetail = (run: CronJobRun) => {
  selectedRun.value = run
  runDetailModalIsOpen.value = true
}

/**
 * Close the full-screen run detail modal.
 */
const closeRunDetail = () => {
  runDetailModalIsOpen.value = false
  selectedRun.value = null
}

/**
 * Open a full-screen error detail modal.
 *
 * @param {CronJobError} error - Selected error.
 */
const openErrorDetail = (error: CronJobError) => {
  selectedError.value = error
  errorDetailModalIsOpen.value = true
}

/**
 * Close the full-screen error detail modal.
 */
const closeErrorDetail = () => {
  errorDetailModalIsOpen.value = false
  selectedError.value = null
}

/**
 * Build the run details shown in the full-screen modal.
 */
const runDetailItems = computed<DetailItem[]>(() => {
  if (!selectedRun.value) return []

  return [
    { label: 'Status', value: formatStatus(selectedRun.value.status) },
    { label: 'Trigger', value: formatStatus(selectedRun.value.triggerSource) },
    { label: 'Started', value: formatDateTime(selectedRun.value.startedAt) },
    { label: 'Finished', value: formatDateTime(selectedRun.value.finishedAt) },
    { label: 'Duration', value: formatDuration(selectedRun.value.durationMs) },
    { label: 'Exit Code', value: selectedRun.value.exitCode ?? '-' },
    { label: 'Cron Job ID', value: selectedRun.value.cronJobId },
    { label: 'Created', value: formatDateTime(selectedRun.value.createdAt) }
  ]
})

/**
 * Build the error details shown in the full-screen modal.
 */
const errorDetailItems = computed<DetailItem[]>(() => {
  if (!selectedError.value) return []

  return [
    { label: 'Category', value: formatStatus(selectedError.value.category) },
    { label: 'Cron Job ID', value: selectedError.value.cronJobId },
    { label: 'Run ID', value: selectedError.value.cronJobRunId ?? '-' },
    { label: 'Created', value: formatDateTime(selectedError.value.createdAt) }
  ]
})

/**
 * Format API status values for display.
 *
 * @param {string | null} status - Raw status value.
 */
const formatStatus = (status: string | null) => {
  if (!status) return '-'

  return status
    .split('_')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/**
 * Return a class list for status pills.
 *
 * @param {string | null} status - Raw status value.
 */
const statusClass = (status: string | null) => {
  const classes: Record<string, string> = {
    success: 'bg-success text-success',
    failed: 'bg-error text-error',
    invalid_config: 'bg-warning text-warning',
    skipped: 'bg-secondary text-secondary'
  }

  return status ? classes[status] || 'bg-secondary text-secondary' : 'bg-secondary text-secondary'
}

/**
 * Format date-time values for table and detail views.
 *
 * @param {string | null} value - ISO date-time value.
 */
const formatDateTime = (value: string | null) => {
  if (!value) return '-'

  return dayjs(value).format('lll')
}

/**
 * Format duration values from milliseconds.
 *
 * @param {number | null} value - Duration in milliseconds.
 */
const formatDuration = (value: number | null) => {
  if (value === null) return '-'
  if (value < 1000) return `${value}ms`

  return `${(value / 1000).toFixed(2)}s`
}

/**
 * Format run output preview lines.
 *
 * @param {string[] | undefined} outputPreview - Output preview from the API.
 */
const formatOutputPreview = (outputPreview: string[] | undefined) => {
  return outputPreview?.length ? outputPreview.join('\n') : '-'
}

/**
 * Format JSON objects for detail display.
 *
 * @param {Record<string, unknown> | null} value - API context object.
 */
const formatJson = (value: Record<string, unknown> | null) => {
  return value ? JSON.stringify(value, null, 2) : '-'
}
</script>

<style scoped>
.cron-fullscreen-backdrop {
  position: fixed;
  inset: 0;
  z-index: 130;
  overflow: auto;
  background: rgba(6, 5, 11, 0.9);
  padding: var(--s-6);
  backdrop-filter: blur(6px);
}

.cron-fullscreen-modal {
  min-height: calc(100vh - (var(--s-6) * 2));
  border-radius: var(--rounded-xl);
  background: var(--bg-primary_alt);
  padding: var(--s-6);
}

.cron-detail-pre {
  max-height: 50vh;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  border-radius: var(--rounded-lg);
  background: var(--bg-secondary);
  padding: var(--s-4);
  font-size: var(--text-sm);
}
</style>
